import {
  AccountSSENotificationDto,
  EAccountUpdateType,
  ENotificationType,
  EBillingTaskStatus,
  EBillingTaskType,
  getCookie,
  HeartbeatEventSource,
  IBaseArrayResultDto,
  IS_LOGGED_COOKIE_NAME,
  jsonParseSafe,
  SSE_ACCOUNT_API_URL,
} from '@skillarea/lib-shared';
import { createStore } from 'zustand';

export type TAccountStoreTexts = {
  failedMessage: string;
  successMessageBilling: string;
};

export type TAccountStoreOptions = {
  onNotification?: (
    notification: AccountSSENotificationDto,
  ) => void | Promise<void>;
};

type TSubscription = { entityId: number; entityName: string };

interface IOnboardStore {
  signOut: () => void;
}

interface ISubscriptionStore {
  getAccountSubscriptions: () => Promise<IBaseArrayResultDto<TSubscription>>;
}

export interface IAccountStore {
  balance: number;
  subscriptions: Partial<Record<string, Record<number, TSubscription>>>;
  texts: TAccountStoreTexts;

  setSubscription: (data: TSubscription) => void;
  setBalance: (balance: number) => void;
  successToast: (task: {
    billingTaskType: EBillingTaskType;
    coins: number;
    context: unknown;
    status: EBillingTaskStatus;
  }) => void;
  errorToast: (message: string) => void;
  getAccountSubscriptions: () => Promise<
    IBaseArrayResultDto<TSubscription> | undefined
  >;
  subscribeAccountEvents: (
    subscriber?: (eventSource: HeartbeatEventSource) => () => void,
  ) => () => void;
}

export type TAccountState = IAccountStore & {
  setSubscriptions: (
    subscriptions: Partial<Record<string, Record<number, TSubscription>>>,
  ) => void;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNotificationType(
  value: unknown,
): value is AccountSSENotificationDto['type'] {
  return (
    value === ENotificationType.achievement ||
    value === ENotificationType.rematch
  );
}

function parseAccountSSENotificationDto(
  data: unknown,
): AccountSSENotificationDto | undefined {
  if (!isRecord(data)) return;

  if (data.updateType !== EAccountUpdateType.notification) return;
  if (typeof data.notificationId !== 'string') return;
  if (!isNotificationType(data.type)) return;
  if (typeof data.message !== 'string') return;
  if (typeof data.createdAt !== 'string') return;
  if (data.expiresAt !== undefined && typeof data.expiresAt !== 'string')
    return;
  if (!isRecord(data.payload)) return;

  // Payload is still validated by caller (type-specific guard).
  return {
    updateType: EAccountUpdateType.notification,
    notificationId: data.notificationId,
    type: data.type,
    message: data.message,
    createdAt: data.createdAt,
    expiresAt: data.expiresAt,
    payload: data.payload as unknown as AccountSSENotificationDto['payload'],
  };
}

export function createAccountStore<Texts extends TAccountStoreTexts>(
  onboardStore: IOnboardStore,
  texts: Texts,
  subscriptionStore?: ISubscriptionStore,
  options?: TAccountStoreOptions,
) {
  return createStore<TAccountState>((set, get) => ({
    balance: 0,
    subscriptions: {},
    texts,

    setSubscriptions(subscriptions) {
      set({ subscriptions });
    },

    setSubscription(data) {
      set((state) => {
        const subscriptions = { ...state.subscriptions };
        if (!subscriptions[data.entityName]) {
          subscriptions[data.entityName] = {};
        }
        (subscriptions[data.entityName] as Record<string, unknown>)[
          data.entityId
        ] = data;
        return { subscriptions };
      });
    },

    successToast(task: {
      billingTaskType: EBillingTaskType;
      coins: number;
      context: unknown;
      status: EBillingTaskStatus;
    }) {
      console.log(
        (get().texts as TAccountStoreTexts).successMessageBilling.replace(
          '{{coins}}',
          String(task.coins),
        ),
      );
    },

    errorToast(message: string) {
      console.error(message);
    },

    setBalance(balance) {
      set({ balance });
    },

    async getAccountSubscriptions() {
      if (!subscriptionStore) return;
      const subs = await subscriptionStore.getAccountSubscriptions();
      if (subs?.success && Array.isArray(subs?.data)) {
        const subscriptions: Record<string, Record<number, TSubscription>> = {};
        subs.data.forEach((sub) => {
          if (!subscriptions[sub.entityName])
            subscriptions[sub.entityName] = {};
          subscriptions[sub.entityName][sub.entityId] = sub;
        });
        get().setSubscriptions(subscriptions);
      }
      return subs;
    },

    subscribeAccountEvents(subscriber) {
      const eventSource = new HeartbeatEventSource(SSE_ACCOUNT_API_URL);

      const unsubscribe = subscriber?.(eventSource);

      const subscriptionListener = async (message: MessageEvent<string>) => {
        const data = jsonParseSafe<TSubscription>(message?.data);
        if (data) get().setSubscription(data);
      };

      const balanceListener = async (message: MessageEvent<string>) => {
        const data = jsonParseSafe<{ balance: number }>(message?.data);
        if (data) get().setBalance(data.balance);
      };

      const taskListener = async (message: MessageEvent<string>) => {
        const data = jsonParseSafe<{
          billingTaskType: EBillingTaskType;
          coins: number;
          context: unknown;
          status: EBillingTaskStatus | string | number;
        }>(message?.data);

        if (!data) return;

        const rawStatus = data?.status;
        const normalizedStatus =
          typeof rawStatus === 'number'
            ? rawStatus
            : typeof rawStatus === 'string'
              ? (() => {
                  const lower = rawStatus.toLowerCase();
                  if (lower === 'failed') return EBillingTaskStatus.failed;
                  if (lower === 'finished') return EBillingTaskStatus.finished;
                  if (lower === 'completed')
                    return EBillingTaskStatus.completed;
                  const asNumber = Number(rawStatus);
                  return Number.isNaN(asNumber) ? undefined : asNumber;
                })()
              : undefined;

        if (normalizedStatus === EBillingTaskStatus.failed) {
          get().errorToast((get().texts as TAccountStoreTexts).failedMessage);
        } else if (
          normalizedStatus === EBillingTaskStatus.finished ||
          normalizedStatus === EBillingTaskStatus.completed
        ) {
          get().successToast({
            ...data,
            status: normalizedStatus,
          });
          if (data.billingTaskType === EBillingTaskType.subscriptionPurchase)
            await get().getAccountSubscriptions();
        }
      };

      const taskStatusEventNames = [
        EAccountUpdateType.taskStatus,
        'task-status',
        'taskstatus',
      ] as const;

      const logoutListener = async (message: MessageEvent<string>) => {
        const data = jsonParseSafe<{ keepSession?: string }>(message?.data);
        if (
          data &&
          (!data.keepSession ||
            data.keepSession !== getCookie(IS_LOGGED_COOKIE_NAME))
        ) {
          await onboardStore.signOut();
        }
      };

      const notificationListener = async (message: MessageEvent<string>) => {
        const parsed = jsonParseSafe<unknown>(message?.data);
        const data = parseAccountSSENotificationDto(parsed);
        if (!data) return;
        await options?.onNotification?.(data);
      };

      eventSource.addEventListener(
        EAccountUpdateType.subscription,
        subscriptionListener,
      );
      eventSource.addEventListener(EAccountUpdateType.balance, balanceListener);
      for (const eventName of taskStatusEventNames) {
        eventSource.addEventListener(eventName, taskListener);
      }
      eventSource.addEventListener(EAccountUpdateType.logout, logoutListener);
      eventSource.addEventListener(
        EAccountUpdateType.notification,
        notificationListener,
      );

      return () => {
        eventSource.removeEventListener(
          EAccountUpdateType.subscription,
          subscriptionListener,
        );
        eventSource.removeEventListener(
          EAccountUpdateType.balance,
          balanceListener,
        );
        for (const eventName of taskStatusEventNames) {
          eventSource.removeEventListener(eventName, taskListener);
        }
        eventSource.removeEventListener(
          EAccountUpdateType.logout,
          logoutListener,
        );
        eventSource.removeEventListener(
          EAccountUpdateType.notification,
          notificationListener,
        );
        unsubscribe?.();
        eventSource.close();
      };
    },
  }));
}
